import { useTranslation } from 'react-i18next';
import {
  ReportPreset,
  SMART_FILTER_FIELDS,
  SmartComparison,
  type SmartBuilderCatalogue,
  type SmartReportFilters,
} from '@optiflow/types';
import { choosableFilters, toRequest, type SmartDraft, type SmartStep } from './smartDraft';
import { codeLabel, dimensionLabel, filterLabel, metricLabel } from './smartLabels';

export interface SmartSummaryChip {
  step: SmartStep;
  label: string;
}

/** What the draft means, in chips; each chip names the step that decides it. */
export function useSummaryChips(
  draft: SmartDraft,
  catalogue: SmartBuilderCatalogue,
): SmartSummaryChip[] {
  const { t } = useTranslation();
  const metric = draft.metric;
  if (!metric) return [];
  const request = toRequest(draft);
  const chips: SmartSummaryChip[] = [{ step: 'metric', label: metricLabel(t, metric) }];
  if (metric.asOfNow) chips.push({ step: 'metric', label: t('reports.asOfNow') });
  else if (draft.period.preset === ReportPreset.CUSTOM) {
    chips.push({
      step: 'period',
      label: t('reports.smart.customRange', {
        from: draft.period.from ?? '',
        to: draft.period.to ?? '',
      }),
    });
  } else chips.push({ step: 'period', label: t(`reports.presets.${draft.period.preset}`) });
  if (metric.comparable && draft.comparison === SmartComparison.PREVIOUS) {
    chips.push({ step: 'period', label: t('reports.smart.comparison.PREVIOUS') });
  }
  const branch = catalogue.branches.find((entry) => entry.id === draft.filters.branchId);
  chips.push({
    step: metric.filters.includes('BRANCH') ? 'filters' : 'metric',
    label: branch ? branch.name : t('reports.smart.allAccessibleBranches'),
  });
  for (const filter of choosableFilters(metric)) {
    if (filter === 'BRANCH') continue;
    const field = SMART_FILTER_FIELDS[filter] as keyof SmartReportFilters;
    const value = draft.filters[field];
    if (value) {
      const name =
        draft.filterNames[field] ?? (filter === 'CURRENCY' ? value : codeLabel(t, filter, value));
      chips.push({ step: 'filters', label: `${filterLabel(t, filter, metric)}: ${name}` });
    }
  }
  if (draft.dimension) {
    chips.push({
      step: 'breakdown',
      label:
        draft.dimension === 'TREND'
          ? dimensionLabel(t, draft.dimension, metric)
          : t('reports.smart.byDimension', {
              dimension: dimensionLabel(t, draft.dimension, metric),
            }),
    });
  }
  if (request?.limit)
    chips.push({ step: 'breakdown', label: t('reports.smart.topN', { count: request.limit }) });
  if (request)
    chips.push({
      step: 'visual',
      label: t(`reports.smart.visualizations.${request.visualization}`),
    });
  return chips;
}
